import { ApplePay } from '@adyen/adyen-web';
/* eslint-disable no-empty-pattern */
import { createSelector, createSlice } from '@reduxjs/toolkit';

import { CheckoutStep, DelayedDeliveryDetails, SlicePayload, UntypedObject, valueof } from '@/types';
import {
  AddressModel,
  CartAttributeKeys,
  CartIncompatibleProduct,
  CartModel,
  CartPayModeModel,
  CartShippingModel,
  OrderModel
} from '@/models';
import { findStepByUrl } from '@/utils/checkoutUtils';
import { spread } from '@/utils/spread';
import { RootState } from '@/redux/create-store';
import { validateDiscountCode } from '@/redux/thunks/checkout/validateDiscountCode';
import { getShippingModesForCartFromCheckout } from '@/redux/thunks/checkout/getShippingModes';
import { getBlockingMotives } from '@/redux/thunks/checkout/getBlockingMotives';
import { redirectToAnotherStep } from '@/redux/thunks/checkout/redirectToAnotherStep';
import { getPaymentModesFromCheckout } from '../thunks/checkout/getPaiementModes';
import { createPaymentProviderForm, loadPaymentProvider } from '@/redux/thunks/checkout/loadPaymentProvider';
import { processPayment } from '@/redux/thunks/checkout/processPayment';
import { PENDING_REQUEST, validatePayment } from '@/redux/thunks/checkout/validatePayment';
import { getLastUserOrder } from '@/redux/thunks/checkout/getLastUserOrder';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { getCartSuccess } from '@/redux/slices/cart.slice';
import { isApplePayEnabled } from '@/utils/payment';
import { getShippingList } from '@/redux/thunks/getShippingList';
import { CHECKOUT_STEPS } from '@/constants/checkoutSteps';
import { PAYMENT_PLATFORMS, STEP_CHANGE, STEPS } from '@/constants/checkout';
import { checkIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/checkIncompatibleProducts';
import { removeIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/removeIncompatibleProducts';
import { COUNTRIES } from '@/constants/profile';
import { isObjectEmpty } from '@/utils/comparisonUtils';

type NextStep = valueof<typeof STEPS> | valueof<typeof STEP_CHANGE>;

export type PaymentProviderShape = {
  label: valueof<typeof PAYMENT_PLATFORMS> | '';
  isLoaded: boolean;
  isReadyToPay: boolean;
  error?: string;
}

export type PaymentStatus = {
  status: 'authorized' | 'refused' | 'pending' | 'error' | 'forwarding' | 'idle';
  redirectUrl?: string;
  error?: string;
}

export type CheckoutState = {
  steps: CheckoutStep[];
  current: CheckoutStep;
  validated: boolean;
  error: any;
  success: boolean;
  next: NextStep;
  order: OrderModel;
  delayedDeliveryDetails: { [idShippingMode: string]: DelayedDeliveryDetails };
  shippingModesForCart: CartShippingModel[];
  incompatibleProducts: CartIncompatibleProduct[];
  checkingIncompatibleProducts: boolean;
  incompatibleSpiritProducts: CartIncompatibleProduct[];
  step: {
    previouslyValidatedSteps: Array<valueof<typeof STEPS>>;
    label: valueof<typeof STEPS>;
  };
  discountCheckStatus: 'idle' | 'validated' | 'errored' | 'pending';
  discountCodeError: 'none' | string;
  shippingModes: Array<CartShippingModel>;
  adminArea: {
    blockingMotives: Array<UntypedObject>;
  }
  paymentModes: Array<CartPayModeModel>;
  paymentProvider: PaymentProviderShape;
  paymentStatus: PaymentStatus;
  applePay: {
    isAvailable: boolean,
    component: ApplePay,
  }
  shippingList: Record<string, CartShippingModel[]>;
  productsPerAvailableTypeModal: {
    isOpen: boolean;
    shipmentNumber: string;
    orderBefore: string;
    deliveryFrom: string;
  };
  selectedDeliveryAvailableType: 'L' | 'PA' | 'P' | '';
  addressSelected: {
    status: 'ok' | 'errored';
    missingFields: AddressFieldKey[];
  }
}

export const initialState: CheckoutState = {
  steps: spread(CHECKOUT_STEPS),
  current: spread(CHECKOUT_STEPS[0]),
  validated: false,
  error: undefined,
  success: false,
  next: undefined,
  order: {} as OrderModel,
  delayedDeliveryDetails: {},
  shippingModesForCart: [],
  incompatibleProducts: [],
  checkingIncompatibleProducts: false,
  incompatibleSpiritProducts: [],
  shippingList: {},
  step: {
    previouslyValidatedSteps: [],
    label: 'cart',
  },
  discountCheckStatus: 'idle',
  discountCodeError: 'none',
  shippingModes: [],
  adminArea: {
    blockingMotives: []
  },
  paymentModes: [],
  paymentProvider: {
    label: '',
    error: undefined,
    isLoaded: false,
    isReadyToPay: false
  },
  paymentStatus: {
    status: 'idle'
  },
  applePay: {
    isAvailable: false,
    component: undefined,
  },
  productsPerAvailableTypeModal: {
    isOpen: false,
    shipmentNumber: '',
    orderBefore: '',
    deliveryFrom: ''
  },
  selectedDeliveryAvailableType: '',
  addressSelected: {
    status: 'ok',
    missingFields: []
  }
};

const checkout = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    storeApplePayComponent: (state, action: SlicePayload<{ component: ApplePay }>) => {
      state.applePay.component = action.payload.component;
    },

    setCurrentStep: (state, action: SlicePayload<{ step: CheckoutStep }>) => {
      state.current = action.payload.step;
    },

    validateStep: (state, action: SlicePayload<{ validated: boolean }>) => {
      state.validated = action.payload.validated;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateStepCart: (_state, _action: SlicePayload<{ update: Partial<CartModel> }>) => {
    },

    changeStep: (state, action: SlicePayload<{ next: NextStep }>) => {
      state.next = action.payload.next;
    },

    changeStepSuccess: (state) => {
      state.error = undefined;
      state.success = true;
    },

    changeStepError: (state, action: SlicePayload<{ error: any }>) => {
      state.error = action.payload.error;
      state.success = false;
    },

    changeStepClear: (state) => {
      state.error = undefined;
      state.success = false;
    },

    connectedSteps: (state) => {
      if (findStepByUrl(state, STEPS.LOGIN)) {
        state.steps = state.steps.filter((step) =>
          step.url !== STEPS.LOGIN
        ).map((step, index) => ({
          ...step,
          id: index
        }));
      }
    },

    disconnectedSteps: (state) => {
      if (!findStepByUrl(state, STEPS.LOGIN)) {
        state.steps = spread(CHECKOUT_STEPS);
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getLastOrder: () => {
    },

    getLastOrderSuccess: (state, action: SlicePayload<{ content: any }>) => {
      state.order = action.payload.content;
    },

    getLastOrderError: (state, _action: SlicePayload<{ error: any }>) => {
      state.order = {} as OrderModel;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getShippingModesForCart: (_state, _action: SlicePayload<{ storeId: string }>) => {
    },

    getShippingModesForCartSuccess: (state, action: SlicePayload<{ shippingsList: CartShippingModel[] }>) => {
      state.shippingModesForCart = action.payload.shippingsList;
    },

    setDelayedDeliveryDetails: (
      state,
      action: SlicePayload<{ delayedDeliveryDetails: { [idShippingMode: string]: DelayedDeliveryDetails }
    }>) => {
      state.delayedDeliveryDetails = action.payload.delayedDeliveryDetails;
    },

    checkCartStorageCompatibility: (state, _action: SlicePayload<{ storeId: string }>) => {
      state.checkingIncompatibleProducts = true;
    },

    setIncompatibleProducts: (state, action: SlicePayload<{ incompatibleProducts: CartIncompatibleProduct[] }>) => {
      state.incompatibleProducts = action.payload.incompatibleProducts;
      state.checkingIncompatibleProducts = false;
    },

    setCheckingIncompatibleProducts: (state, _action: SlicePayload<{ incompatibleProducts: CartIncompatibleProduct[] }>) => {
      state.checkingIncompatibleProducts = true;
    },

    setServerSideStep: (state, action: SlicePayload<valueof<typeof STEPS>>) => {
      if (!Object.values(STEPS).includes(action.payload)) {
        return;
      }
      state.step.label = action.payload;
      state.step.previouslyValidatedSteps = generateStepProgress(action.payload);
    },

    validateCurrentCheckoutStep: (state, action: SlicePayload<valueof<typeof STEPS>>) => {
      if (action.payload) {
        state.step.label = action.payload;
        state.step.previouslyValidatedSteps = generateStepProgress(action.payload);
      }
      state.paymentProvider.isLoaded = false;
    },

    setPaymentInfosAre: (state, action: SlicePayload<'valid' | 'invalid'>) => {
      state.paymentProvider.isReadyToPay = action.payload === 'valid';
    },

    setPaymentStatusInsideCheckout: (state, action: SlicePayload<PaymentStatus>) => {
      state.paymentStatus = action.payload;
    },

    isApplePayAvailable: (state, action: SlicePayload<{isApplePayAvailable: boolean}>) => {
      state.applePay.isAvailable = action.payload.isApplePayAvailable;
    },

    reloadApplePay: (state) => {
      state.paymentProvider.isReadyToPay = false;
      state.paymentProvider.isLoaded = false;
      state.applePay.isAvailable = false;
      state.applePay.component.unmount();
    },

    showProductsPerAvailableTypeModal:
      (state, action: SlicePayload<{
        shouldOpen: boolean, shippingId: 'L' | 'PA' | 'P' | '', shipmentNumber: string
        orderBefore: string, deliveryFrom: string
      }>) => {
        state.productsPerAvailableTypeModal.isOpen = action.payload.shouldOpen;
        state.productsPerAvailableTypeModal.shipmentNumber = action.payload.shipmentNumber;
        state.productsPerAvailableTypeModal.orderBefore = action.payload.orderBefore;
        state.productsPerAvailableTypeModal.deliveryFrom = action.payload.deliveryFrom;
        state.selectedDeliveryAvailableType = action.payload.shippingId;
      },

    resetAddressErrors: (state) => {
      state.addressSelected = initialState.addressSelected;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(validateDiscountCode.pending, (state) => {
      state.discountCheckStatus = 'pending';
      state.discountCodeError = 'none';
    });

    builder.addCase(validateDiscountCode.fulfilled, (state, action) => {
      if (action.payload.discountCode) {
        state.discountCheckStatus = 'validated';
        return;
      }
      state.discountCheckStatus = 'idle';
    });

    builder.addCase(validateDiscountCode.rejected, (state, action) => {
      state.discountCheckStatus = 'errored';
      state.discountCodeError = action.payload.error.errorCode;
    });

    builder.addCase(getShippingModesForCartFromCheckout.fulfilled, (state, action: UntypedObject) => {
      state.shippingModes = action.payload;
    });

    builder.addCase(getShippingModesForCartFromCheckout.rejected, (state) => {
      state.shippingModes = [];
    });

    builder.addCase(checkIncompatibleProductsFromCheckout.fulfilled, (state, action: UntypedObject) => {
      state.incompatibleSpiritProducts = action.payload;
    });

    builder.addCase(checkIncompatibleProductsFromCheckout.rejected, (state) => {
      state.incompatibleSpiritProducts = [];
    });

    builder.addCase(removeIncompatibleProductsFromCheckout.fulfilled, (state, action: UntypedObject) => {
      state.incompatibleSpiritProducts = [];
    });

    builder.addCase(getBlockingMotives.fulfilled, (state, action: UntypedObject) => {
      state.adminArea.blockingMotives = action.payload;
    });

    builder.addCase(getBlockingMotives.rejected, (state) => {
      state.adminArea.blockingMotives = [];
    });

    builder.addCase(redirectToAnotherStep.pending, (state, action: UntypedObject) => {
      state.step.label = action.meta.arg;
    });

    builder.addCase(getPaymentModesFromCheckout.fulfilled, (state, action) => {
      state.paymentModes = action.payload;
    });

    builder.addCase(getPaymentModesFromCheckout.rejected, (state) => {
      state.paymentModes = [];
    });

    builder.addCase(getCartSuccess, (state, action) => {
      if (action.payload.content?.discountCode?.name) {
        state.discountCheckStatus = 'validated';
      }
      if (['NO_VALIDATION', 'PAY_JUNCTION', 'ADYEN_TWINT', 'CART_ZERO'].includes(action.payload.content?.paiementMode?.action)) {
        state.paymentProvider.isReadyToPay = true;
      }
    });

    builder.addCase(loadPaymentProvider.rejected, (state, action) => {
      state.paymentProvider.isLoaded = false;
      state.paymentProvider.error = action.payload.message;
    });

    builder.addCase(loadPaymentProvider.pending, (state, action) => {
      state.paymentProvider.label = action.meta.arg.paymentProvider;
      state.paymentProvider.isLoaded = false;
      state.paymentProvider.error = undefined;

      const paymentProvider = action.meta.arg.paymentProvider;

      if (paymentProvider === PAYMENT_PLATFORMS.NO_VALIDATION ||
        paymentProvider === PAYMENT_PLATFORMS.PAY_JUNCTION ||
        paymentProvider === PAYMENT_PLATFORMS.CSR_PAY_JUNCTION ||
        paymentProvider === PAYMENT_PLATFORMS.CART_ZERO) {
        state.paymentProvider.isReadyToPay = true;
        state.paymentProvider.label = paymentProvider;
      }
    });

    builder.addCase(updateCartAttributeByName.pending, (state, action) => {
      const { attributeName, newValue } =
        action.meta.arg as { attributeName: CartAttributeKeys, newValue: CartPayModeModel | AddressModel };
      if (attributeName === 'shipping' || (attributeName === 'billing' && !isObjectEmpty(newValue))) {
        state.addressSelected.missingFields = handleAddressMissingFields(newValue as AddressModel, attributeName === 'billing');
        state.addressSelected.status = state.addressSelected.missingFields.length === 0 ? 'ok' : 'errored';
      }

      if (attributeName === 'paiementMode' && action.meta.arg.newValue.pickId !== PAYMENT_PLATFORMS.HIPAY) {
        state.paymentProvider.isReadyToPay = false;
        state.paymentProvider.label = '';
      }
      if (attributeName === 'paiementMode' && (
        action.meta.arg.newValue.action === PAYMENT_PLATFORMS.NO_VALIDATION ||
        action.meta.arg.newValue.action === PAYMENT_PLATFORMS.PAY_JUNCTION ||
        action.meta.arg.newValue.action === PAYMENT_PLATFORMS.ADYEN_TWINT ||
        action.meta.arg.newValue.action === PAYMENT_PLATFORMS.CSR_PAY_JUNCTION ||
        action.meta.arg.newValue.action === PAYMENT_PLATFORMS.CART_ZERO
      )) {
        state.paymentProvider.isReadyToPay = true;
        state.paymentProvider.label = action.meta.arg.newValue.action;
      }
    });

    builder.addCase(processPayment.pending, (state) => {
      if (state.paymentProvider.label === PAYMENT_PLATFORMS.HIPAY) {
        state.paymentStatus = { status: 'pending' };
      }
    });

    builder.addCase(processPayment.rejected, (state) => {
      state.paymentStatus = { status: 'error' };
    });

    builder.addCase(validatePayment.pending, (state) => {
      state.paymentStatus = { status: 'pending' };
    });

    builder.addCase(validatePayment.rejected, (state, action) => {
      if (action.payload?.cause !== PENDING_REQUEST) {
        state.paymentStatus.status = 'error';
      }
    });

    builder.addCase(validatePayment.fulfilled, (state, action) => {
      state.paymentStatus.status = 'idle';
    });

    builder.addCase(getLastUserOrder.fulfilled, (state, action) => {
      state.order = action.payload.order;
      if (action.payload.order.discountCode?.name) {
        state.discountCheckStatus = 'validated';
      }
    });

    builder.addCase(getLastUserOrder.rejected, (state) => {
      state.order = {} as OrderModel;
    });

    builder.addCase(createPaymentProviderForm.fulfilled, (state) => {
      if (state.paymentProvider.label === 'ADYEN_APPLEPAY') {
        state.paymentProvider.isReadyToPay = true;
      }
      state.paymentProvider.isLoaded = true;
    });
    builder.addCase(getShippingList.fulfilled, (state, action) => {
      state.shippingList = action.payload;
    });

    builder.addCase(getShippingList.rejected, (state) => {
      state.shippingList = {};
    });
  }
});

export const {
  setCurrentStep, validateStep, updateStepCart, setPaymentStatusInsideCheckout,
  changeStep, changeStepSuccess, changeStepError, changeStepClear,
  connectedSteps, disconnectedSteps,
  getLastOrder, getLastOrderSuccess, getLastOrderError,
  getShippingModesForCart, getShippingModesForCartSuccess,
  setDelayedDeliveryDetails, reloadApplePay, storeApplePayComponent,
  showProductsPerAvailableTypeModal, resetAddressErrors,
  checkCartStorageCompatibility, setIncompatibleProducts, setCheckingIncompatibleProducts,
  setServerSideStep, validateCurrentCheckoutStep, setPaymentInfosAre, isApplePayAvailable
} = checkout.actions;

export default checkout.reducer;

const selectCheckoutState = (state: RootState) => state.checkout;
const selectStoreId = (state: RootState) => state.root.storeConf?.storeId;

export const selectCurrentStepLabel = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.step.label
);

export const selectPreviouslyValidatedSteps = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.step.previouslyValidatedSteps
);

export const selectDiscountCheckStatus = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.discountCheckStatus
);

export const selectDiscountFormErrorCode = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.discountCodeError
);

export const selectAvailableShippingModes = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.shippingModes
);

export const selectAvailablePaymentModes = createSelector(
  [selectStoreId, selectCheckoutState],
  (storeId, checkoutState) => {
    if (typeof window === 'undefined' || !isApplePayEnabled(storeId, window as any)) {
      return checkoutState.paymentModes.filter((paymentMode) => paymentMode.action !== PAYMENT_PLATFORMS.ADYEN_APPLEPAY);
    }
    return checkoutState.paymentModes;
  }
);

export const selectBlockingMotives = (state: RootState) => state.checkout.adminArea.blockingMotives;

const generateStepProgress = (step: valueof<typeof STEPS>): Array<valueof<typeof STEPS>> => {
  switch (step) {
    case 'cart':
      return [];
    case 'login':
      return ['cart'];
    case 'address':
      return ['cart'];
    case 'billing':
      return ['cart', 'address'];
    case 'shipping':
      return ['cart', 'address', 'billing'];
    case 'review':
      return ['cart', 'address', 'billing', 'shipping'];
    case 'pay':
      return ['cart', 'address', 'billing', 'shipping', 'review'];
    case 'report':
      return ['cart', 'address', 'billing', 'shipping', 'review', 'pay'];
  }
};

export const selectPaymentProviderInfos = (state: RootState) => state.checkout.paymentProvider;

export const selectPaymentStatus = (state: RootState) => state.checkout.paymentStatus.status;

export const selectPaymentModes = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.paymentModes
);

export const selectLastOrder = createSelector(
  [selectCheckoutState],
  (checkoutState) => checkoutState.order
);

export const lastOrderHasStorageProducts = (state: RootState) => state.checkout.order?.hasStorageProducts;

export const checkIfStepIsEligibleForTaxFreeBilling = (state: RootState) => {
  switch (state.checkout.step.label) {
    case 'review':
    case 'pay':
    case 'report':
      return true;
    default:
      return false;
  }
};

export const selectOrderContainsAvailableForDeliveryProducts = (state: RootState) => state.checkout.order.products.some(
  (orderProduct) => orderProduct.product.item.attributes.in_stock.valueId !== 'P'
);

export const selectShippingList = createSelector(
  [selectCheckoutState],
  (checkout) => {
    const order = { L: 0, PA: 1, P: 2 };
    return Object.fromEntries(
      Object.entries(checkout.shippingList).sort(
        ([keyA], [keyB]) => order[keyA] - order[keyB]
      )
    );
  }
);

export const selectIsAddressOk = createSelector(
  [selectCheckoutState],
  (checkout) => {
    return checkout.addressSelected.status === 'ok';
  }
);

export const selectAddressSelectedStatus = createSelector(
  [selectCheckoutState],
  (checkout) => (checkout.addressSelected.status === 'errored' && checkout.addressSelected.missingFields.length > 0)
);

const handleAddressMissingFields = (address: AddressModel, isBilling = false) => {
  const missingFields: AddressFieldKey[] = [];

  if (!address.firstName) missingFields.push('firstName');
  if (!address.lastName) missingFields.push('lastName');
  if (!address.street) missingFields.push('street');
  if (!address.country) missingFields.push('country');
  if (!address.city) missingFields.push('city');

  if (!address.phoneNumber && !address.phoneNumberMobile && !address.phoneNumberWork) {
    missingFields.push('phoneNumber');
  }

  if (address.country === COUNTRIES.USA && !address.state) {
    missingFields.push('state');
  }

  if (address.country !== COUNTRIES.HONGKONG && !address.postalCode) {
    missingFields.push('postalCode');
  }

  if (
    isBilling &&
    ([COUNTRIES.SPAIN, COUNTRIES.ITALY, COUNTRIES.PORTUGAL] as string[]).includes(address.country) &&
    !address.fiscalCode
  ) {
    missingFields.push('fiscalCode');
  }

  if (address.isCompany && !address.company) {
    missingFields.push('company');
  }

  return missingFields;
};

type AddressFieldKey =
  | 'firstName'
  | 'lastName'
  | 'street'
  | 'country'
  | 'city'
  | 'phoneNumber'
  | 'state'
  | 'postalCode'
  | 'fiscalCode'
  | 'company';
